import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type {
  CreateSubMerchantPayload,
  MerchantDto,
} from "@/features/merchant/api/dto";

/**
 * Crée un sous-marchand rattaché à un master
 * (`POST /Merchants/{masterId}/sub-merchants?actingMerchantId=...`).
 * Idempotence OBLIGATOIRE (guide §4.7).
 */
export const useCreateSubMerchant = (masterId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "sub-merchants", "create", masterId],
    mutationFn: ({
      body,
      actingMerchantId,
    }: {
      body: CreateSubMerchantPayload;
      actingMerchantId?: string;
    }) => {
      const query = actingMerchantId
        ? `?actingMerchantId=${actingMerchantId}`
        : "";
      return mutateIdempotent<MerchantDto>({
        scopeId: `merchant.sub-merchants.add.${masterId}`,
        url: `${merchantEndpoints.merchants.createSubMerchant(masterId)}${query}`,
        body,
      });
    },
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        merchantWriteCacheKeys(masterId),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateSubMerchant failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
